import React, { HTMLAttributes, ReactNode, forwardRef, useContext, useMemo } from 'react';

import { ThemeContext } from 'styled-components';
import TableContext from '../_TableContext/TableContext';
import TableHeadContext from '../_TableHeadContext';
import { StyledHead } from './TableHead.styles';

export interface TableHeadProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  /** Use a transparent background. */
  transparent?: boolean;
  /** Display a divider to the bottom of the head. */
  divider?: boolean;
  /** Aligns the text to the left. */
  textAlign?: string;
  /** Background of the head. */
  background?: string;
  /** Fix height of the head. */
  height?: number;
  /** Text color  of the head. */
  color?: string;
  sticky?: boolean;
}

const TableHeadDefaultProps = {
  transparent: false,
  divider: false,
};

/**
 * @mode developer
 */
const TableHead = Object.assign(
  forwardRef<HTMLDivElement, TableHeadProps>(({ transparent, divider, textAlign, color, background, height, ...props }, ref) => {
    const { displayMode } = useContext(TableContext);
    const theme = useContext(ThemeContext);
    const tableHeadContext = useMemo(() => ({
      isHead: true,
      textAlign: textAlign || 'center',
      color,
      background: background || (transparent ? 'transparent' : theme.colors.smoothGray),
    }), [textAlign, color, background, transparent, theme.colors.smoothGray]);

    return (
      <TableHeadContext.Provider value={tableHeadContext}>
        <StyledHead
          role="rowgroup"
          transparent={transparent}
          divider={divider}
          color={color}
          displayMode={displayMode}
          background={background}
          height={height}
          ref={ref}
          {...props}
        />
      </TableHeadContext.Provider>
    );
  }),
  {
    defaultProps: TableHeadDefaultProps,
    type: Symbol('Table.Head'),
  },
);

export default TableHead;
